import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import OverflowingText from '../../components/foundations/OverflowingText';
import Button from '../../components/atoms/Button';
import Tooltip from '../../components/molecules/Tooltip';
import VerdictBadge from './VerdictBadge';
import VerdictBody from './VerdictBody';
import MonitorMenu from './MonitorMenu';
import PromptPreview from './PromptPreview';
import { CARD_STATES, MENU_ACTION_ICONS, TRANSLATION_KEY, VERDICT_OUTCOMES } from '../constants';
import { monitorShape, verdictShape } from '../propShapes';
import { formatShortScheduleLabel } from '../utils';
import SlackIcon from '../../resources/svg/icons/slack-icon.svg';
import css from './MonitorCard.css';

const CARD_KEY = `${TRANSLATION_KEY}.CARD`;

const stopCardClick = event => event.stopPropagation();

const MENU_ACTION_COMPONENTS = Object.fromEntries(
    Object.entries(MENU_ACTION_ICONS).map(([key, icon]) => [
        key,
        props => <Icon name={icon} size={IconSizes.X_SMALL} interactive={false} {...props} />,
    ])
);

const MonitorCard = ({
    monitor,
    cardState,
    verdict = null,
    hasUpdate = false,
    justAdded = false,
    onOpenThread,
    onRunNow,
    onOpenMonitorDialog,
    onPauseToggle,
    onRemove,
    onConnectSlack,
    onDisconnectSlack,
    slackConnecting = false,
    translate,
}) => {
    const slackChannel = monitor.slack_channels?.[0];
    const schedule = formatShortScheduleLabel(monitor.schedule);
    const isStarting = cardState === CARD_STATES.QUEUED;
    const isRunning = cardState === CARD_STATES.RUNNING;
    const isBusy = cardState === CARD_STATES.BUSY;
    const isActive = isStarting || isRunning;
    const isPaused = cardState === CARD_STATES.PAUSED;
    // A pause taken mid-run leaves the claim in place while the check finishes server-side.
    const isFinishingCheck = isPaused && monitor.claimed_at != null;
    const isPromptUnclear = !isActive && verdict?.outcome === VERDICT_OUTCOMES.PROMPT_UNCLEAR;
    const showNew = hasUpdate && !isActive && !isPromptUnclear;
    const showNotification = verdict?.should_notify;

    const menuActions = [
        { key: 'EDIT_PROMPT', onClick: onOpenMonitorDialog },
        { key: isPaused ? 'RESUME' : 'PAUSE', onClick: () => onPauseToggle(!isPaused) },
        ...(slackChannel ? [{ key: 'DISCONNECT_SLACK', onClick: onDisconnectSlack }] : []),
        { key: 'REMOVE', onClick: onRemove },
    ].map(({ key, onClick }) => ({
        key,
        title: translate(`${TRANSLATION_KEY}.MENU.${key}`),
        onClick,
        ActionComponent: MENU_ACTION_COMPONENTS[key],
    }));

    return (
        <div
            data-testid="monitor-card"
            data-state={cardState}
            className={classNames(css.card, { [css.attention]: isPromptUnclear || showNew })}
            onClick={onOpenThread}
        >
            <div className={css.leftPane}>
                <div className={css.titleRow}>
                    <div className={classNames(css.title, { [css.untitled]: !monitor.title })}>
                        {monitor.title || translate(`${CARD_KEY}.UNTITLED`)}
                    </div>
                    {justAdded && (
                        <span className={css.justAddedBadge}>
                            <Translate id={`${CARD_KEY}.JUST_ADDED`} />
                        </span>
                    )}
                </div>
                <PromptPreview prompt={monitor.user_query} className={css.promptBlock} onClick={stopCardClick} />
                <div className={css.actionRow} onClick={stopCardClick}>
                    <Tooltip title={schedule.title}>
                        <span className={css.schedule}>
                            <Icon
                                name="clock"
                                size={IconSizes.X_SMALL}
                                interactive={false}
                                variant={IconVariants.LIGHT}
                                className={css.scheduleIcon}
                            />
                            {schedule.label}
                        </span>
                    </Tooltip>
                    <div className={css.activitySlot}>
                        {isStarting && (
                            <span className={css.activityState}>
                                <Icon
                                    name="hourglass"
                                    size={IconSizes.X_SMALL}
                                    interactive={false}
                                    variant={IconVariants.LIGHT}
                                    className={classNames(css.activityIcon, css.hourglass)}
                                />
                                <Translate id={`${CARD_KEY}.STARTING_SOON`} />
                            </span>
                        )}
                        {isRunning && (
                            <span className={css.activityState}>
                                <span className={classNames(css.activityIcon, css.spinner)} />
                                <Translate id={`${CARD_KEY}.RUNNING_NOW`} />
                            </span>
                        )}
                        {isFinishingCheck && (
                            <span className={css.activityState}>
                                <span className={classNames(css.activityIcon, css.spinner)} />
                                <Translate id={`${CARD_KEY}.FINISHING_CHECK`} />
                            </span>
                        )}
                        {isPaused && !isFinishingCheck && (
                            <Tooltip titleTranslationKey={`${CARD_KEY}.RESUME_TOOLTIP`}>
                                <button
                                    type="button"
                                    className={css.pauseToggle}
                                    aria-label={translate(`${CARD_KEY}.RESUME_TOOLTIP`)}
                                    onClick={() => onPauseToggle(false)}
                                >
                                    <span className={css.faces}>
                                        <span className={classNames(css.face, css.pausedFace)}>
                                            <Icon
                                                name="player-pause"
                                                size={IconSizes.X_SMALL}
                                                interactive={false}
                                                className={classNames(css.activityIcon, css.faceIcon)}
                                            />
                                            <Translate id={`${CARD_KEY}.PAUSED`} />
                                        </span>
                                        <span className={classNames(css.face, css.resumeFace)} aria-hidden="true">
                                            <Icon
                                                name="player-play"
                                                size={IconSizes.X_SMALL}
                                                interactive={false}
                                                className={classNames(css.activityIcon, css.faceIcon)}
                                            />
                                            <Translate id={`${CARD_KEY}.RESUME`} />
                                        </span>
                                    </span>
                                </button>
                            </Tooltip>
                        )}
                        {isBusy && (
                            <Tooltip titleTranslationKey={`${CARD_KEY}.THREAD_BUSY_TOOLTIP`}>
                                <span className={css.activityState}>
                                    <span className={classNames(css.activityIcon, css.spinner)} />
                                    <Translate id={`${CARD_KEY}.THREAD_BUSY`} />
                                </span>
                            </Tooltip>
                        )}
                        {!isActive && !isBusy && !isPaused && (
                            <Button
                                type="link"
                                className={css.linkAction}
                                Icon="refresh"
                                iconSize={IconSizes.X_SMALL}
                                iconVariant={IconVariants.ACTIVE}
                                iconClass={css.linkIcon}
                                onClick={onRunNow}
                            >
                                <Translate id={`${CARD_KEY}.RUN_NOW`} />
                            </Button>
                        )}
                    </div>
                    <Button
                        type="link"
                        className={css.linkAction}
                        Icon="pencil"
                        iconSize={IconSizes.X_SMALL}
                        iconVariant={IconVariants.ACTIVE}
                        iconClass={css.linkIcon}
                        onClick={onOpenMonitorDialog}
                    >
                        <Translate id={`${CARD_KEY}.EDIT_MONITOR`} />
                    </Button>
                </div>
            </div>
            <div className={classNames(css.rightPane, { [css.actionsOnly]: !verdict && !showNew })}>
                <div className={css.badgeSlot}>
                    <VerdictBadge verdict={verdict} />
                    {showNotification && (
                        <span className={css.checkingChip}>
                            <Icon
                                name="bell"
                                type={IconTypes.TABLER}
                                size={IconSizes.XX_SMALL}
                                interactive={false}
                                variant={IconVariants.LIGHT}
                            />
                            <Translate id={`${CARD_KEY}.FLAGGED_FOR_YOU`} />
                        </span>
                    )}
                    {showNew && (
                        <span className={css.newBadge}>
                            <Translate id={`${CARD_KEY}.NEW`} />
                        </span>
                    )}
                    <div className={css.cardActions}>
                        {slackConnecting ? (
                            <span className={classNames(css.slackIcon, css.spinner)} />
                        ) : (
                            <Tooltip
                                titleTranslationKey={
                                    slackChannel ? `${CARD_KEY}.SLACK_CONNECTED_TOOLTIP` : `${CARD_KEY}.CONNECT_SLACK`
                                }
                                titleTranslationData={{ channel: slackChannel?.channel_name }}
                            >
                                <SlackIcon
                                    className={css.slackIcon}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onConnectSlack(monitor);
                                    }}
                                />
                            </Tooltip>
                        )}
                        {slackChannel && (
                            <OverflowingText className={css.slackChannelBadge}>
                                {slackChannel.channel_name}
                            </OverflowingText>
                        )}
                        <MonitorMenu actions={menuActions} />
                    </div>
                </div>
                <VerdictBody
                    isPromptUnclear={isPromptUnclear}
                    verdict={verdict}
                    nextRunAt={monitor.next_run_at}
                    checkInProgress={isActive || isFinishingCheck}
                />
            </div>
        </div>
    );
};

MonitorCard.propTypes = {
    monitor: monitorShape.isRequired,
    cardState: PropTypes.oneOf(Object.values(CARD_STATES)).isRequired,
    verdict: verdictShape,
    hasUpdate: PropTypes.bool,
    justAdded: PropTypes.bool,
    onOpenThread: PropTypes.func.isRequired,
    onRunNow: PropTypes.func.isRequired,
    onOpenMonitorDialog: PropTypes.func.isRequired,
    onPauseToggle: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onConnectSlack: PropTypes.func.isRequired,
    onDisconnectSlack: PropTypes.func.isRequired,
    slackConnecting: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(MonitorCard);
